import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import { colors, borderRadius, shadows, spacing } from '../styles/designTokens';
import { formatNumber } from '../utils/numberFormatter';
import { formatIsoDateOnlyDisplay } from '../utils/dateUtils';

export interface WeeklyDailySeries {
  date: string; // YYYY-MM-DD
  amount: number;
}

export interface WeeklyPaymentPointDeductionChartProps {
  rangeLabel: string; // yy.mm.dd ~ yy.mm.dd (표시용)
  previousPayments: WeeklyDailySeries[];
  currentPayments: WeeklyDailySeries[];
  previousPointDeductions: WeeklyDailySeries[];
  currentPointDeductions: WeeklyDailySeries[];
  // 금색 기준선(라인) 값: 차트 내 각 날짜별 월 누적 결제 금액(취소 제외)
  monthlyCumulativePaymentsSeries: Array<{ date: string; amount: number }>;
  /** 특정 일(열) 클릭 시 — 해당 일이 속한 달(YYYY-MM) 기준 결제 목록 API 호출 */
  onChartDayClick?: (isoDate: string) => void;
  /** 결제 목록·월 결제 카드와 연동된 선택 일(YYYY-MM-DD) — 해당 열 강조 */
  highlightedIsoDate?: string | null;
}

const Container = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[5]};
  border: 1px solid ${colors.semantic.border.primary};
  box-shadow: ${shadows.sm};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: ${spacing[4]};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  row-gap: 6px;
`;

const LegendItem = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 800;
  color: ${colors.secondary[600]};

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: ${props => props.$color};
  }
`;

const LegendLineItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 800;
  color: ${colors.secondary[600]};
`;

const LegendLineSwatch = styled.span<{ $color: string }>`
  width: 16px;
  height: 3px;
  border-radius: 2px;
  background: ${props => props.$color};
  display: inline-block;
`;

const ChartWrap = styled.div<{ $clickable?: boolean }>`
  width: 100%;
  height: 240px;
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};
  @media (max-width: 640px) {
    height: 220px;
  }
`;

const ChartClickSurface = styled.div<{ $clickable: boolean }>`
  border-radius: ${borderRadius.lg};
  outline: none;
  background: transparent;
  border: none;

  ${p =>
    p.$clickable &&
    css`
      &:focus-visible {
        outline: 2px solid ${colors.primary[300]};
        outline-offset: 2px;
      }
    `}
`;

const normalizeDateKey = (raw: string) => {
  if (!raw) return '';
  const m = String(raw).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : raw;
};

const formatDayLabel = (isoDate: string) => formatIsoDateOnlyDisplay(isoDate);

/** 단일 좌측 Y축: 결제 막대·포인트 차감 막대·월 누적 라인 공통 스케일 — 피크 높이 비율 */
const LEFT_PEAK_HEIGHT_RATIO = 0.88;

/**
 * Y축: 데이터 최댓값이 `peakHeightRatio`만큼의 "높이 비율"을 쓰도록 역산한 뒤,
 * 상한만 1·2·5·10×10ⁿ 눈금으로 올림 (낯선 숫자·틱 방지).
 * peakHeightRatio 0.88 → 최댓값이 축의 ~88% 지점, 위 ~12%는 시각 여백.
 */
const buildRatioValueAxis = (maxValue: number, peakHeightRatio: number) => {
  if (maxValue <= 0) {
    return { domain: [0, 1] as [number, number], ticks: [0, 1] };
  }

  const ratio = Math.min(Math.max(peakHeightRatio, 0.62), 0.94);
  const rawTop = maxValue / ratio;
  const cap = rawTop * 1.03;

  const targetDivisions = 4;
  const roughStep = cap / targetDivisions;

  const exp = Math.floor(Math.log10(Math.max(roughStep, 1e-12)));
  const magnitude = Math.pow(10, exp);
  const f = roughStep / magnitude;

  let niceCoeff = 1;
  if (f <= 1) niceCoeff = 1;
  else if (f <= 2) niceCoeff = 2;
  else if (f <= 5) niceCoeff = 5;
  else niceCoeff = 10;

  const step = niceCoeff * magnitude;
  const axisMax = Math.ceil(cap / step) * step;
  const tickCount = Math.round(axisMax / step) + 1;
  const ticks = Array.from({ length: Math.min(tickCount, 12) }, (_, i) => i * step);

  return {
    domain: [0, axisMax] as [number, number],
    ticks
  };
};

type ChartRow = {
  isoDate: string;
  dayLabel: string;
  payment: number;
  point: number;
  monthlyCum: number;
};

const TooltipPanel = styled.div`
  min-width: 180px;
  padding: 10px 12px;
  border-radius: ${borderRadius.md};
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.semantic.border.primary};
  box-shadow: ${shadows.md};
  font-size: 12px;
`;

const TooltipTitle = styled.div`
  font-weight: 800;
  color: ${colors.semantic.text.primary};
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  color: ${colors.semantic.text.secondary};
`;

const TooltipRowLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
`;

const TooltipSwatch = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const TooltipRowValue = styled.span`
  font-weight: 800;
  color: ${colors.semantic.text.primary};
  font-variant-numeric: tabular-nums;
`;

function WeeklyChartTooltip({
  active,
  label,
  payload
}: {
  active?: boolean;
  label?: string;
  payload?: Array<{ payload?: ChartRow }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  const titleDate =
    row.isoDate ||
    normalizeDateKey(String(label ?? '')) ||
    String(label ?? '');

  return (
    <TooltipPanel>
      <TooltipTitle>{titleDate}</TooltipTitle>
      <TooltipRow>
        <TooltipRowLabel>
          <TooltipSwatch $color={colors.primary[500]} />
          결제 금액
        </TooltipRowLabel>
        <TooltipRowValue>{formatNumber(row.payment)}원</TooltipRowValue>
      </TooltipRow>
      <TooltipRow>
        <TooltipRowLabel>
          <TooltipSwatch $color={colors.error[500]} />
          포인트 차감
        </TooltipRowLabel>
        <TooltipRowValue>{formatNumber(row.point)}P</TooltipRowValue>
      </TooltipRow>
      <TooltipRow>
        <TooltipRowLabel>
          <TooltipSwatch $color={colors.warning[500]} />
          월 누적 결제
        </TooltipRowLabel>
        <TooltipRowValue>{formatNumber(row.monthlyCum)}원</TooltipRowValue>
      </TooltipRow>
    </TooltipPanel>
  );
}

export const WeeklyPaymentPointDeductionChart: React.FC<
  WeeklyPaymentPointDeductionChartProps
> = ({
  currentPayments,
  currentPointDeductions,
  monthlyCumulativePaymentsSeries,
  onChartDayClick,
  highlightedIsoDate = null
}) => {
  const clickable = typeof onChartDayClick === 'function';
  const chartData = useMemo(() => {
    // 결제·포인트 차감 막대와 월 누적 라인은 동일 좌측 Y축 스케일을 사용합니다.
    const len = 7;

    const getAmount = (arr: WeeklyDailySeries[], i: number) => arr[i]?.amount ?? 0;
    const getDate = (arr: WeeklyDailySeries[], i: number) => arr[i]?.date ?? '';

    const monthlyByDate = Object.fromEntries(
      monthlyCumulativePaymentsSeries.map(x => [normalizeDateKey(x.date), Number(x.amount)])
    );

    return Array.from({ length: len }).map((_, i) => {
      const paymentCur = getAmount(currentPayments, i);
      const pointCur = getAmount(currentPointDeductions, i);

      const isoDateRaw = getDate(currentPayments, i) || getDate(currentPointDeductions, i);
      const isoDate = normalizeDateKey(isoDateRaw);

      return {
        isoDate,
        dayLabel: isoDate ? formatDayLabel(isoDate) : '',
        payment: paymentCur,
        point: pointCur,
        monthlyCum: Number(monthlyByDate[isoDate] ?? 0)
      };
    });
  }, [
    currentPayments,
    currentPointDeductions,
    monthlyCumulativePaymentsSeries
  ]);

  const axis = useMemo(() => {
    const maxLeft = Math.max(
      0,
      ...chartData.map(d =>
        Math.max(d.monthlyCum ?? 0, d.payment ?? 0, d.point ?? 0)
      )
    );

    const left = buildRatioValueAxis(maxLeft, LEFT_PEAK_HEIGHT_RATIO);

    return { left };
  }, [chartData]);

  const highlightedDayLabel = useMemo(() => {
    if (!highlightedIsoDate) return null;
    return chartData.find(d => d.isoDate === highlightedIsoDate)?.dayLabel ?? null;
  }, [chartData, highlightedIsoDate]);

  return (
    <Container>
      <Header>
        <Legend>
          <LegendItem $color={colors.primary[500]}>
            <span className="dot" /> 결제 금액
          </LegendItem>
          <LegendItem $color={colors.error[500]}>
            <span className="dot" /> 포인트 차감
          </LegendItem>
          <LegendLineItem>
            <LegendLineSwatch $color={colors.warning[500]} />
            월 누적 결제
          </LegendLineItem>
        </Legend>
      </Header>

      <ChartClickSurface
        $clickable={clickable}
        role={clickable ? 'group' : undefined}
        aria-label={
          clickable
            ? '주간 결제 차트, 마우스를 올리면 일별 수치 안내를 볼 수 있고, 날짜 열을 클릭하면 해당 달 결제 목록을 아래에 표시합니다'
            : '주간 결제 차트, 마우스를 올리면 일별 수치 안내를 볼 수 있습니다'
        }
      >
        <ChartWrap $clickable={clickable}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 12, right: 8, left: 2, bottom: 8 }}
              onClick={
                clickable
                  ? (next: { activeTooltipIndex?: number }) => {
                      const i = next?.activeTooltipIndex;
                      if (typeof i === 'number' && i >= 0 && chartData[i]?.isoDate) {
                        onChartDayClick(chartData[i].isoDate);
                      }
                    }
                  : undefined
              }
            >
            <CartesianGrid strokeDasharray="3 3" stroke={colors.secondary[200]} opacity={0.45} />
            <XAxis
              dataKey="dayLabel"
              tick={{ fontSize: 11, fill: colors.secondary[600], fontWeight: 700 }}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              tick={{ fontSize: 11, fill: colors.secondary[600], fontWeight: 700 }}
              axisLine={false}
              tickLine={false}
              domain={axis.left.domain}
              ticks={axis.left.ticks}
              allowDecimals={false}
              tickFormatter={(value: number) => formatNumber(value)}
            />
            <Tooltip
              shared
              isAnimationActive={false}
              cursor={{ stroke: colors.secondary[400], strokeWidth: 1, strokeDasharray: '4 4' }}
              content={WeeklyChartTooltip}
            />
            {highlightedDayLabel ? (
              <ReferenceArea
                yAxisId="left"
                x1={highlightedDayLabel}
                x2={highlightedDayLabel}
                y1={0}
                y2={axis.left.domain[1]}
                fill="rgba(59, 130, 246, 0.14)"
                stroke="none"
                ifOverflow="visible"
              />
            ) : null}
            <Bar
              yAxisId="left"
              dataKey="payment"
              name="결제 금액"
              fill={colors.primary[500]}
              barSize={26}
              radius={[3, 3, 0, 0]}
              activeBar={false}
              onClick={
                clickable
                  ? (data: { payload?: ChartRow }, _i: number, e: React.MouseEvent) => {
                      e.stopPropagation();
                      const iso = data?.payload?.isoDate;
                      if (iso) onChartDayClick(iso);
                    }
                  : undefined
              }
            >
              {chartData.map((row, i) => {
                const hi = Boolean(highlightedIsoDate && row.isoDate === highlightedIsoDate);
                return (
                  <Cell
                    key={`pay-${row.isoDate || i}`}
                    fill={hi ? colors.primary[600] : colors.primary[500]}
                  />
                );
              })}
            </Bar>
            <Bar
              yAxisId="left"
              dataKey="point"
              name="포인트 차감"
              fill={colors.error[500]}
              barSize={26}
              radius={[3, 3, 0, 0]}
              activeBar={false}
              onClick={
                clickable
                  ? (data: { payload?: ChartRow }, _i: number, e: React.MouseEvent) => {
                      e.stopPropagation();
                      const iso = data?.payload?.isoDate;
                      if (iso) onChartDayClick(iso);
                    }
                  : undefined
              }
            >
              {chartData.map((row, i) => {
                const hi = Boolean(highlightedIsoDate && row.isoDate === highlightedIsoDate);
                return (
                  <Cell
                    key={`pt-${row.isoDate || i}`}
                    fill={hi ? colors.error[600] : colors.error[500]}
                  />
                );
              })}
            </Bar>
            <Line
              type="monotone"
              yAxisId="left"
              dataKey="monthlyCum"
              stroke={colors.warning[500]}
              strokeWidth={2.5}
              dot={(props: {
                cx?: number;
                cy?: number;
                payload?: ChartRow;
              }) => {
                const { cx, cy, payload } = props;
                if (cx == null || cy == null) return <g />;
                const hi = Boolean(highlightedIsoDate && payload?.isoDate === highlightedIsoDate);
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={hi ? 4.5 : 3}
                    fill={colors.warning[500]}
                    stroke="#fff"
                    strokeWidth={hi ? 2 : 1}
                    style={{ cursor: clickable ? 'pointer' : undefined }}
                    onClick={
                      clickable
                        ? e => {
                            e.stopPropagation();
                            const iso = payload?.isoDate;
                            if (iso) onChartDayClick(iso);
                          }
                        : undefined
                    }
                  />
                );
              }}
              activeDot={false}
              connectNulls
              isAnimationActive={false}
            />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartWrap>
      </ChartClickSurface>
    </Container>
  );
};

export default WeeklyPaymentPointDeductionChart;

